import { Component, Input, OnChanges } from '@angular/core'
import { ChartConfiguration } from 'chart.js'

import { SmartLink } from '../../../shared/interfaces/smart-link.interface'
import { AggregationDataset } from '../../../shared/interfaces/visualizations/aggregation-dataset.interface'
import { ColorService } from '../../../shared/services/color.service'
import { SmartLinkService } from '../../../shared/services/smart-link.service'
import { ChartItem } from './interfaces/chart-item.interface'

@Component({
  selector: 'app-stacked-bar',
  templateUrl: './stacked-bar.component.html',
  styleUrls: ['./stacked-bar.component.scss']
})
export class StackedBarComponent implements OnChanges {
  @Input() dataset: AggregationDataset

  barChartData: ChartConfiguration<'bar'>['data']
  barChartOptions: any = {
    responsive: true,
    tooltips: {
      mode: 'index',
      intersect: false
    },
    scales: {
      y: {
        id: 'yAxis1',
        title: {
          display: true
        }
      },
      x: {
        id: 'xAxis1'
      },
      x2: {
        id: 'xAxis2',
        type: 'category',
        grid: {
          tickWidth: (element: { index: number; tick: { label: string } }) => {
            const groupLabel: string =
              this.dataset.data[element.index]?.groupLabel
            const previousGroupLabel: string =
              this.dataset.data[element.index - 1]?.groupLabel

            return groupLabel !== previousGroupLabel ? 2 : 0
          }
        },
        ticks: {
          align: 'right',
          callback: (_value: string, index: number) => {
            const groupLabel: string = this.dataset.data[index].groupLabel

            const previousGroupLabel: string =
              this.dataset.data[index - 1]?.groupLabel

            return groupLabel !== previousGroupLabel ? groupLabel : ''
          }
        }
      }
    }
  }

  possibleValues: { label: string; color?: string }[]
  // We use "any" instead of ChartDataset<'bar', any>[] to attach "link" to the dataset.
  datasets: any[]

  constructor(private smartLinkService: SmartLinkService) {}

  ngOnChanges() {
    this.barChartOptions.scales.y.title.text = this.dataset.yAxisLabel

    // Group data by groupLabel.
    const groupedData: { [key: string]: ChartItem[] } =
      this.dataset.data.reduce((acc, d) => {
        if (!acc[d.groupLabel]) {
          acc[d.groupLabel] = []
        }
        acc[d.groupLabel].push(d)
        return acc
      }, {})

    // Match each subItem with a color.
    const subItems: string[] = [
      ...new Set(
        [].concat(
          ...this.dataset.data.map((chartItem: ChartItem) =>
            chartItem.subItems.map((sI, i) => sI.label)
          )
        )
      )
    ]

    this.possibleValues = subItems.map((subItem: string, i: number) => ({
      label: subItem,
      color: ColorService.getColors()[i]
    }))

    this.datasets = this.possibleValues.map((possibleValue) => ({
      label: possibleValue.label,
      data: this.dataset.data.map((chartItem: ChartItem) => {
        const subItemData = chartItem.subItems.find(
          (sI) => sI.label === possibleValue.label
        )
        return subItemData ? subItemData.count : 0
      }),
      links: this.dataset.data.map((chartItem: ChartItem) => {
        const subItemData = chartItem.subItems.find(
          (sI) => sI.label === possibleValue.label
        )
        return subItemData ? subItemData.link : null
      }),
      backgroundColor: possibleValue.color,
      borderColor: possibleValue.color,
      stack: 'stack1'
    }))

    this.barChartData = {
      labels: this.dataset.data.map((d) => d.label),
      datasets: this.datasets
    }
  }

  onChartClick(event: any) {
    const clickedElement = event.active[0]
    if (!clickedElement) {
      return
    }
    const link: SmartLink =
      this.datasets[clickedElement.datasetIndex].links[clickedElement.index]

    if (link) {
      this.smartLinkService.goToLink(link)
    }
  }
}
